import './styles.scss'
import React, { memo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDropzone } from 'react-dropzone'
import { uploadItemStatus } from '../../../ImageBrowser/constants'
import Loader from '../../../../../Shared/Loader'
import activityTrackingService from '../../../../../../services/activityTrackingService'
import { trackingEvent } from '../../../../../../constants'

const RoomImage = memo(({ image }) => {
  return (
    <div className={'room-image-browser-item'}>
      <div className="room-image-browser-item-image-container">
        <div className={'room-image-browser-item-image'}
             style={{ backgroundImage: 'url(' + image.displayUrl + ')' }}/>
      </div>
    </div>
  )
})

const RoomImageUploadItem = memo((
  {
    image,
    onUploadCancel,
    onRemove
  }) => {
  switch (image.status) {
    case uploadItemStatus.uploading:
      if (image.progress === 100) {
        return (
          <div className="room-image-browser-item-preview">
            <div className="room-image-browser-item-image-preview-container">
              <div className="room-image-browser-item-image-preview image-browser-item-image-preview-blurred"
                   style={{ backgroundImage: 'url(' + image.src + ')' }}/>
              <div className="room-image-browser-item-image-preview-content">
                <div className="middle-content">Processing</div>
              </div>
              <div className="top-right-content">
                <span className={'icon-item-image-preview-close'}
                      onClick={() => onUploadCancel(image)}/>
              </div>
            </div>
          </div>
        )
      }
      return (
        <div className="room-image-browser-item-preview">
          <div className="room-image-browser-item-image-preview-container">
            <div className="room-image-browser-item-image-preview image-browser-item-image-preview-blurred"
                 style={{ backgroundImage: 'url(' + image.src + ')' }}/>
            <div className="room-image-browser-item-image-preview-content">
              <div className="middle-content">
                <div>{image.progress}%</div>
                <Loader size={32}/>
              </div>
            </div>
          </div>
          <div className="top-right-content">
                <span className={'icon-item-image-preview-close'}
                      onClick={() => onUploadCancel(image)}/>
          </div>
        </div>
      )
    case uploadItemStatus.waiting:
      return (
        <div className="room-image-browser-item-preview">
          <div className="room-image-browser-item-image-preview-container"
               title={`File ${image.name} is queued.`}>
            <div className="room-image-browser-item-image-preview-content">
              <div className="middle-content">Queued</div>
              <div className="top-right-content">
                <span className={'icon-item-image-preview-close'}
                      onClick={() => onUploadCancel(image)}/>
              </div>
            </div>
          </div>
        </div>
      )
    case uploadItemStatus.errorProcessImage:
      return (
        <div className="room-image-browser-item-preview">
          <div className="room-image-browser-item-image-preview-container"
               title={`File ${image.name} could not be processed. We
                recommend jpg files only.`}>
            <div className="room-image-browser-item-image-preview-content">
              <div className="middle-content">
                <span className={'icon-item-image-preview-isolation-mode'}/>
              </div>
              <div className="top-right-content">
                <span className={'icon-item-image-preview-close'}
                      onClick={() => onRemove(image)}/>
              </div>
            </div>
          </div>
        </div>
      )
    case uploadItemStatus.error:
      return (
        <div className="room-image-browser-item-preview">
          <div className="room-image-browser-item-image-preview-container"
               title={`File ${image.name} could not be processed.`}>
            <div className="room-image-browser-item-image-preview-content">
              <div className="middle-content">
                <span className={'icon-item-image-preview-isolation-mode'}/>
              </div>
              <div className="top-right-content">
                <span className={'icon-item-image-preview-close'}
                      onClick={() => onRemove(image)}/>
              </div>
            </div>
          </div>
        </div>
      )
    default:
      return null
  }
})

export default function RoomImageBrowser (
  {
    room,
    images,
    uploadQueue,
    onUploadQueueImageRemove,
    onUploadFiles
  }
) {
  const { t } = useTranslation()

  const onDrop = useCallback(files => {
    activityTrackingService.trackEvent(trackingEvent.uploadImages, {
      giataId: room.giataId,
      numberOfImages: files.length,
      page: 'Edit Rooms'
    })
    onUploadFiles(files, room.id)
  }, [onUploadFiles, room])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ['image/*']
  })

  const imagesWithUploadQueueLength = images.length + uploadQueue.length
  const getUploadContainerClasses = () => {
    const classNames = [
      'room-image-browser-upload-container',
      imagesWithUploadQueueLength < 5 ? 'room-image-browser-upload-container-big' : 'room-image-browser-upload-container-small',
      imagesWithUploadQueueLength > 0 && 'room-image-browser-upload-container-align-right',
      isDragActive && 'room-image-browser-upload-container-drag-active',
    ]
    return classNames.filter(Boolean).join(' ')
  }

  return (
    <div className={'room-image-browser'}>
      <div>
        {
          images.map((image, index) => (
            <RoomImage key={index} image={image}/>
          ))
        }
        {
          uploadQueue.map((image, index) => {
            return (
              <RoomImageUploadItem image={image} key={index + '_imageToUpload'}
                                   onUploadCancel={onUploadQueueImageRemove}
                                   onRemove={onUploadQueueImageRemove}/>
            )
          })
        }
      </div>
      <div
        className={getUploadContainerClasses()}>
        <div className={
          `room-image-browser-upload-button`}
             {...getRootProps()}>
          <input {...getInputProps()} />
          <div className={'room-image-browser-upload-button-icon'}></div>
          <div className={'room-image-browser-upload-button-desc'}>
            <span>{t('ADD_IMAGES')}</span>
            <span>{t('ADD_IMAGES_DESC')}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
